import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Space,
  Button,
  Tag,
  Modal,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Input,
  Select,
  Descriptions,
  Divider,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  UndoOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import {
  JournalEntry,
  Journal,
  CreateJournalEntryInput,
  journalService
} from '@/services/journalService';
import { Account } from '@/services/accountService';
import JournalEntryForm from './JournalEntryForm';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface JournalEntriesProps {
  journals: Journal[];
  accounts: Account[];
}

const JournalEntries: React.FC<JournalEntriesProps> = ({ journals, accounts }) => {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<JournalEntry[]>([]);

  // Modal states
  const [entryModalVisible, setEntryModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [reverseModalVisible, setReverseModalVisible] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [journalFilter, setJournalFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = [...entries];

    // State filter
    if (stateFilter !== 'all') {
      filtered = filtered.filter(entry => entry.state === stateFilter);
    }

    // Journal filter
    if (journalFilter !== 'all') {
      filtered = filtered.filter(entry => entry.journalId === journalFilter);
    }

    // Date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].startOf('day');
      const endDate = dateRange[1].endOf('day');
      filtered = filtered.filter(entry => {
        const entryDate = dayjs(entry.date);
        return entryDate.isAfter(startDate) && entryDate.isBefore(endDate);
      });
    }

    // Search filter
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.name.toLowerCase().includes(search) ||
        (entry.ref && entry.ref.toLowerCase().includes(search)) ||
        (entry.narration && entry.narration.toLowerCase().includes(search))
      );
    }

    setFilteredEntries(filtered);
  }, [entries, searchText, stateFilter, journalFilter, dateRange]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const data = await journalService.getJournalEntries();
      setEntries(data);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch journal entries');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntry = () => {
    console.log('🔘 New Entry button clicked');
    console.log('Journals:', journals.length);
    console.log('Accounts:', accounts.length);

    // Validate data is loaded
    if (journals.length === 0) {
      message.error('No journals available. Please create journals first.');
      console.error('❌ No journals loaded');
      return;
    }

    if (accounts.length === 0) {
      message.error('No accounts available. Please create accounts first.');
      console.error('❌ No accounts loaded');
      return;
    }

    console.log('✅ Data validation passed');
    setSelectedEntry(null);
    setIsEditing(false);
    setEntryModalVisible(true);
    console.log('✅ Modal visibility set to true');
  };

  const handleEditEntry = (entry: JournalEntry) => {
    if (entry.state !== 'DRAFT') {
      message.warning('Only draft entries can be edited');
      return;
    }
    setSelectedEntry(entry);
    setIsEditing(true);
    setEntryModalVisible(true);
  };

  const handleViewEntry = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setViewModalVisible(true);
  };

  const handleSubmitEntry = async (values: CreateJournalEntryInput) => {
    setLoading(true);
    try {
      if (isEditing && selectedEntry) {
        await journalService.updateJournalEntry(selectedEntry.id, values);
        message.success('Journal entry updated successfully');
      } else {
        await journalService.createJournalEntry(values);
        message.success('Journal entry created successfully');
      }
      setEntryModalVisible(false);
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to save journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handlePostEntry = async (entry: JournalEntry) => {
    setLoading(true);
    try {
      await journalService.postJournalEntry(entry.id);
      message.success('Journal entry posted successfully');
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to post journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEntry = async (entry: JournalEntry) => {
    setLoading(true);
    try {
      await journalService.cancelJournalEntry(entry.id);
      message.success('Journal entry cancelled successfully');
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to cancel journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handleReverseEntry = async (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setReverseModalVisible(true);
  };

  const handleConfirmReverse = async () => {
    if (!selectedEntry) return;

    setLoading(true);
    try {
      await journalService.reverseJournalEntry(selectedEntry.id, {
        reversalDate: new Date().toISOString(),
        narration: `Reversal of ${selectedEntry.name}`
      });
      message.success('Reversal entry created successfully (in DRAFT state)');
      setReverseModalVisible(false);
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to reverse journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (entry: JournalEntry) => {
    setLoading(true);
    try {
      await journalService.deleteJournalEntry(entry.id);
      message.success('Journal entry deleted successfully');
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete journal entry');
    } finally {
      setLoading(false);
    }
  };

  const getStateTag = (state: string) => {
    const stateColors: Record<string, string> = {
      DRAFT: 'default',
      POSTED: 'success',
      CANCELLED: 'error'
    };
    return <Tag color={stateColors[state] || 'default'}>{state}</Tag>;
  };

  const columns: ColumnsType<JournalEntry> = [
    {
      title: 'Entry #',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      render: (name: string) => <Text code>{name}</Text>
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Journal',
      key: 'journal',
      width: 150,
      render: (_, record) => (
        record.journal ? (
          <div>
            <Tag color="blue">{record.journal.code}</Tag>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.journal.name}
            </Text>
          </div>
        ) : null
      )
    },
    {
      title: 'Reference',
      dataIndex: 'ref',
      key: 'ref',
      width: 120,
      render: (ref: string) => ref || '-'
    },
    {
      title: 'Description',
      dataIndex: 'narration',
      key: 'narration',
      ellipsis: true,
      render: (narration: string) => narration || '-'
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const total = record.lines.reduce((sum, line) => sum + Number(line.debit), 0);
        return <Text strong>${total.toFixed(2)}</Text>;
      }
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      filters: [
        { text: 'Draft', value: 'DRAFT' },
        { text: 'Posted', value: 'POSTED' },
        { text: 'Cancelled', value: 'CANCELLED' }
      ],
      onFilter: (value, record) => record.state === value,
      render: (state: string) => getStateTag(state)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <Space size={2} wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewEntry(record)}
          >
            View
          </Button>
          {record.state === 'DRAFT' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditEntry(record)}
              >
                Edit
              </Button>
              <Popconfirm
                title="Post this entry?"
                description="Once posted, the entry cannot be edited."
                onConfirm={() => handlePostEntry(record)}
                okText="Post"
                cancelText="Cancel"
              >
                <Button
                  type="link"
                  size="small"
                  icon={<CheckOutlined />}
                  style={{ color: '#52c41a' }}
                >
                  Post
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Delete this entry?"
                onConfirm={() => handleDeleteEntry(record)}
                okText="Delete"
                cancelText="Cancel"
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                >
                  Delete
                </Button>
              </Popconfirm>
            </>
          )}
          {record.state === 'POSTED' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<UndoOutlined />}
                onClick={() => handleReverseEntry(record)}
              >
                Reverse
              </Button>
              <Popconfirm
                title="Cancel this entry?"
                description="This will mark the entry as cancelled."
                onConfirm={() => handleCancelEntry(record)}
                okText="Cancel Entry"
                cancelText="No"
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                >
                  Cancel
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>Journal Entries</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchEntries}>
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateEntry}
              >
                New Entry
              </Button>
            </Space>
          </Col>
        </Row>

        <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Input
                placeholder="Search entries..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                prefix={<EyeOutlined />}
              />
            </Col>
            <Col span={6}>
              <Select
                style={{ width: '100%' }}
                placeholder="Filter by state"
                value={stateFilter}
                onChange={setStateFilter}
              >
                <Select.Option value="all">All States</Select.Option>
                <Select.Option value="DRAFT">Draft</Select.Option>
                <Select.Option value="POSTED">Posted</Select.Option>
                <Select.Option value="CANCELLED">Cancelled</Select.Option>
              </Select>
            </Col>
            <Col span={6}>
              <Select
                style={{ width: '100%' }}
                placeholder="Filter by journal"
                value={journalFilter}
                onChange={setJournalFilter}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  { value: 'all', label: 'All Journals' },
                  ...journals.map(j => ({
                    value: j.id,
                    label: `${j.code} - ${j.name}`
                  }))
                ]}
              />
            </Col>
            <Col span={6}>
              <RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
                format="YYYY-MM-DD"
                placeholder={['Start Date', 'End Date']}
                allowClear
              />
            </Col>
          </Row>
        </Space>

        <Table
          dataSource={filteredEntries}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} entries`
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={isEditing ? 'Edit Journal Entry' : 'Create Journal Entry'}
        open={entryModalVisible}
        onCancel={() => setEntryModalVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <JournalEntryForm
          entry={selectedEntry || undefined}
          journals={journals}
          accounts={accounts}
          onSubmit={handleSubmitEntry}
          onCancel={() => setEntryModalVisible(false)}
          loading={loading}
        />
      </Modal>

      {/* View Details Modal */}
      <Modal
        title="Journal Entry Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedEntry && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Entry Number">{selectedEntry.name}</Descriptions.Item>
              <Descriptions.Item label="State">{getStateTag(selectedEntry.state)}</Descriptions.Item>
              <Descriptions.Item label="Date">
                {dayjs(selectedEntry.date).format('YYYY-MM-DD')}
              </Descriptions.Item>
              <Descriptions.Item label="Journal">
                {selectedEntry.journal?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Reference">{selectedEntry.ref || '-'}</Descriptions.Item>
              <Descriptions.Item label="Created By">{selectedEntry.createdBy}</Descriptions.Item>
              {selectedEntry.postedBy && (
                <>
                  <Descriptions.Item label="Posted By">{selectedEntry.postedBy}</Descriptions.Item>
                  <Descriptions.Item label="Posted At">
                    {dayjs(selectedEntry.postedAt).format('YYYY-MM-DD HH:mm')}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="Description" span={2}>
                {selectedEntry.narration || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Entry Lines</Divider>

            <Table
              dataSource={selectedEntry.lines}
              size="small"
              pagination={false}
              rowKey="id"
              columns={[
                {
                  title: 'Account',
                  key: 'account',
                  render: (_, line) => (
                    <div>
                      <Text strong>{line.account?.code}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {line.account?.name}
                      </Text>
                    </div>
                  )
                },
                {
                  title: 'Description',
                  dataIndex: 'name',
                  key: 'name',
                  render: (name: string) => name || '-'
                },
                {
                  title: 'Debit',
                  dataIndex: 'debit',
                  key: 'debit',
                  align: 'right',
                  render: (debit: number) => (
                    <Text strong style={{ color: debit > 0 ? '#3f8600' : undefined }}>
                      {debit > 0 ? `$${Number(debit).toFixed(2)}` : '-'}
                    </Text>
                  )
                },
                {
                  title: 'Credit',
                  dataIndex: 'credit',
                  key: 'credit',
                  align: 'right',
                  render: (credit: number) => (
                    <Text strong style={{ color: credit > 0 ? '#cf1322' : undefined }}>
                      {credit > 0 ? `$${Number(credit).toFixed(2)}` : '-'}
                    </Text>
                  )
                }
              ]}
            />
          </div>
        )}
      </Modal>

      {/* Reverse Confirmation Modal */}
      <Modal
        title="Reverse Journal Entry"
        open={reverseModalVisible}
        onOk={handleConfirmReverse}
        onCancel={() => setReverseModalVisible(false)}
        confirmLoading={loading}
      >
        <p>Are you sure you want to reverse this entry?</p>
        <p>
          A new journal entry will be created with debits and credits swapped. The new entry will be
          created in DRAFT state for your review before posting.
        </p>
      </Modal>
    </div>
  );
};

export default JournalEntries;